import moment from 'moment';
import Decimal from 'decimal.js';
import { Location } from 'react-router-dom';
import { Invoice, InvoiceStatusCountResponse, InvoicesStatuses } from 'store/invoices/types';
import {
    DashboardInvoicesData,
    initialInvoicesDashboardData,
    InvoiceDetailsTabs,
    InvoiceFieldValues,
    SubmitInvoiceChargeFormFields,
} from './types';
import { CreateChargePayload } from 'store/invoice-config/types';

export const RE_SEND_INVOICE_STATUSES = [
    InvoicesStatuses.DRAFT_NON_EDITABLE,
    InvoicesStatuses.AWAITING_PAYMENT,
    InvoicesStatuses.PAID,
    InvoicesStatuses.PARTIALLY_PAID,
    InvoicesStatuses.OVERDUE,
];
export const NO_DUE_INVOICE_STATUSES = [InvoicesStatuses.DRAFT, InvoicesStatuses.PAID, InvoicesStatuses.CANCELLED];

const invoiceStatusToDashboardField: Partial<Record<InvoicesStatuses, keyof DashboardInvoicesData>> = {
    [InvoicesStatuses.DRAFT]: 'draft',
    [InvoicesStatuses.PAID]: 'paymentReceived',
    [InvoicesStatuses.AWAITING_PAYMENT]: 'toBePaid',
    [InvoicesStatuses.TO_BE_DUE]: 'aboutToBeDue',
    [InvoicesStatuses.CANCELLED]: 'cancelled',
    [InvoicesStatuses.OVERDUE]: 'overdue',
    [InvoicesStatuses.PARTIALLY_PAID]: 'partiallyPaid',
};

export const mapInvoiceStatusCountsToDashboardData = (counts: InvoiceStatusCountResponse[]): DashboardInvoicesData => {
    const invoicesCountData: DashboardInvoicesData = { ...initialInvoicesDashboardData };
    let all = 0;

    counts.forEach(({ status, count }) => {
        const field = invoiceStatusToDashboardField[status];
        if (field) invoicesCountData[field] = count;
        if (status !== InvoicesStatuses.TO_BE_DUE) all += count;
    });

    return { ...invoicesCountData, all };
};

export const getActiveTab = (location: Location, invoiceId: string | undefined) => {
    const defaultTab = InvoiceDetailsTabs.OVERVIEW;

    if (!invoiceId || !location) {
        return defaultTab;
    }

    const pathname = location.pathname.replace(/\/+$/, '');

    if (pathname.endsWith(`${invoiceId}/history`)) {
        return InvoiceDetailsTabs.HISTORY;
    }

    return defaultTab;
};

export const getDueFlags = (dueDate: string, dueDaysCountForWarning: number, status: InvoicesStatuses) => {
    if (!dueDate || NO_DUE_INVOICE_STATUSES.includes(status)) {
        return { isOverdue: false, isDueSoon: false, diffDays: null };
    }

    const now = moment().startOf('day');
    const due = moment(dueDate).startOf('day');
    const diffDays = due.diff(now, 'days');

    const isOverdue = diffDays < 0 || status === InvoicesStatuses.OVERDUE;
    const isDueSoon = !isOverdue && diffDays <= dueDaysCountForWarning;

    return { isOverdue, isDueSoon, diffDays };
};

// The due date is a calendar day, so it is sent as UTC midnight to keep the BE's toLocalDate() on the picked day
export const toUtcMidnight = (date: string | moment.Moment) => `${moment(date).format('YYYY-MM-DD')}T00:00:00Z`;

export const initialInvoiceFieldValues: InvoiceFieldValues = {
    invoiceDetails: {
        invoiceNumber: '',
        orderNumber: '',
        mrn: '',
        jobRefNumber: '',
        dueDate: '',
    },
    customerDetails: {
        name: '',
        address: '',
        city: '',
        postCode: '',
        country: '',
    },
    taxLines: [],
    additionalCharges: [],
};

export const mapInvoiceToInitialValues = (invoice: Invoice): InvoiceFieldValues => {
    // Older invoices and BEs that don't send a recipient yet fall back to the customer
    const recipient = invoice.recipient ?? invoice.customer;
    const recipientAddr = recipient?.address;
    return {
        invoiceDetails: {
            invoiceNumber: invoice.invoiceNumber?.toString() ?? '',
            orderNumber: invoice.orderNumber ?? null,
            mrn: invoice.mrn ?? null,
            jobRefNumber: invoice.jobReferenceId ?? null,
            dueDate: invoice.dueDate ? moment.utc(invoice.dueDate).format('YYYY-MM-DD') : '',
        },
        customerDetails: {
            name: recipient?.name ?? null,
            address: recipientAddr?.addressLine1 ?? null,
            city: recipientAddr?.city ?? null,
            postCode: recipientAddr?.postCode ?? null,
            country: recipientAddr?.country ?? null,
        },
        taxLines: invoice.taxLines ?? [],
        additionalCharges: invoice.additionalCharges ?? [],
    };
};

export const buildFeeLineDescription = (
    previousDescription: string | undefined,
    percentage: string | number,
    minAmount: string | number,
    currencySymbol: string
) => {
    const prefix = extractPrefix(previousDescription);

    return `${prefix} ${fmtPercent(percentage)} per Transaction (Minimum charge ${fmtMoney(
        minAmount,
        currencySymbol
    )})`;
};

const fmtPercent = (x: string | number | null | undefined) => `${new Decimal(x ?? 0).toDecimalPlaces(2).toNumber()}%`;

const fmtMoney = (x: string | number | null | undefined, currencySymbol: string) =>
    `${currencySymbol}${new Decimal(x ?? 0).toDecimalPlaces(2).toFixed(2)}`;

// Pulls the fixed prefix (everything before the number) if present
const extractPrefix = (desc?: string) => {
    if (!desc) return 'Use of JMC TAN Account';
    // e.g. "Use of JMC TAN Account 2% per Transaction (Minimum charge €50.00)"
    // Grab leading text up to the first percent chunk
    const m = desc.match(/^(.+?)\s*\d+(\.\d+)?\s*%\s*per\s*Transaction/i);
    return m?.[1]?.trim() || 'Use of JMC TAN Account';
};

export const toAdditionalChargePayload = (values: SubmitInvoiceChargeFormFields): CreateChargePayload => {
    if (values.amount == null) {
        throw new Error('Amount is required');
    }

    const payload: CreateChargePayload = {
        productCode: values.productCode,
        description: values.description,
        nominalCode: values.nominalCode,
        taxCode: values.taxCode,
        amount: values.amount,
        percentage: values.percentage,
    };

    return payload;
};

export const invoiceStatusColorMap: Record<InvoicesStatuses, string> = {
    [InvoicesStatuses.AWAITING_PAYMENT]: 'var(--blue-6)',
    [InvoicesStatuses.DRAFT]: 'var(--gray-6)',
    [InvoicesStatuses.DRAFT_NON_EDITABLE]: 'var(--gray-6)',
    [InvoicesStatuses.PAID]: 'var(--polar-green-6)',
    [InvoicesStatuses.OVERDUE]: 'var(--dust-red-6)',
    [InvoicesStatuses.PARTIALLY_PAID]: 'var(--polar-green-8)',
    [InvoicesStatuses.CANCELLED]: 'var(--dust-red-8)',
    [InvoicesStatuses.TO_BE_DUE]: 'var(--orange-6)',
    [InvoicesStatuses.DISPUTED]: 'var(--cyan-6)',
};
